import { useState } from "react";
import type { QuestionBlock } from "@shared/lessonBlocks";
import { gradeQuestionBlock } from "@shared/lessonBlocks";
import { Input } from "@/components/ui/input";
import { Check, X } from "lucide-react";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, type CheckResult } from "../CheckShell";

interface Props {
  block: QuestionBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

export function QuestionBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const [single, setSingle] = useState<string | null>(null);
  const [multi, setMulti] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");

  const response = () => {
    switch (block.kind) {
      case "mc":
      case "tf": return single;
      case "multi": return Array.from(multi);
      case "numeric":
      case "text": return text;
    }
  };
  const canSubmit = block.kind === "multi" ? multi.size > 0
    : block.kind === "numeric" ? /\d/.test(text)          // a lone "-" from the sign toggle is not an answer
    : block.kind === "text" ? text.trim().length > 0
    : single !== null;
  const submit = () => check.submit(gradeQuestionBlock(block, response()));
  const done = check.locked;
  const correctIds = new Set(Array.isArray(block.answer) ? block.answer.map(String) : [String(block.answer)]);

  const optionState = (id: string): "neutral" | "selected" | "correct" | "wrong" => {
    const chosen = block.kind === "multi" ? multi.has(id) : single === id;
    if (done) {
      if (correctIds.has(id)) return "correct";
      if (chosen) return "wrong";
      return "neutral";
    }
    if (check.status === "wrong" && chosen && !correctIds.has(id)) return "wrong";
    return chosen ? "selected" : "neutral";
  };
  const optionCls = (state: ReturnType<typeof optionState>) => ({
    neutral: "border-border bg-card hover:border-primary/50 hover:bg-primary/5",
    selected: "border-primary bg-primary/10 ring-2 ring-primary/30",
    correct: "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30",
    wrong: "border-rose-400 bg-rose-50 dark:bg-rose-900/30",
  })[state];

  const letters = ["A", "B", "C", "D", "E", "F"];

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      {block.imageUrl && <img src={block.imageUrl} alt="" className="rounded-2xl max-h-64 mx-auto object-contain" loading="lazy" />}

      {(block.kind === "mc" || block.kind === "multi") && block.options && (
        <div className="grid gap-2.5">
          {block.options.map((opt, i) => {
            const state = optionState(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                disabled={done}
                onClick={() => {
                  if (check.status === "wrong") check.retry();
                  if (block.kind === "multi") setMulti(prev => { const n = new Set(prev); n.has(opt.id) ? n.delete(opt.id) : n.add(opt.id); return n; });
                  else setSingle(opt.id);
                }}
                className={`w-full text-left rounded-2xl border-2 p-3.5 flex items-center gap-3 transition-all active:scale-[0.99] ${optionCls(state)}`}
                data-testid={`option-${opt.id}`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${state === "correct" ? "bg-emerald-500 text-white" : state === "wrong" ? "bg-rose-500 text-white" : state === "selected" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {state === "correct" ? <Check className="w-4 h-4" /> : state === "wrong" ? <X className="w-4 h-4" /> : letters[i]}
                </span>
                <RichText text={opt.text} inline className="text-base" />
              </button>
            );
          })}
          {block.kind === "multi" && !done && <p className="text-xs text-muted-foreground">Pick every answer that is correct.</p>}
        </div>
      )}

      {block.kind === "tf" && (
        <div className="grid grid-cols-2 gap-3">
          {(["true", "false"] as const).map(v => {
            const state = optionState(v);
            return (
              <button
                key={v}
                type="button"
                disabled={done}
                onClick={() => { if (check.status === "wrong") check.retry(); setSingle(v); }}
                className={`rounded-2xl border-2 p-5 text-xl font-extrabold transition-all active:scale-[0.98] ${optionCls(state)}`}
                data-testid={`option-${v}`}
              >
                {v === "true" ? "✅ True" : "❌ False"}
              </button>
            );
          })}
        </div>
      )}

      {(block.kind === "numeric" || block.kind === "text") && (
        <div className="max-w-sm flex items-center gap-2">
          {/* The phone keypad a numeric answer opens (iOS Decimal Pad, Android numeric) has no minus
              key, so a negative answer is untypeable without this toggle. */}
          {block.kind === "numeric" && (
            <button
              type="button"
              disabled={done}
              onClick={() => {
                if (check.status === "wrong") check.retry();
                setText(t => (t.startsWith("-") ? t.slice(1) : `-${t}`));
              }}
              className={`h-14 px-4 rounded-2xl border-2 font-bold text-lg flex-shrink-0 transition-colors active:scale-95 disabled:opacity-50 ${text.startsWith("-") ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/50"}`}
              aria-label="Add or remove the minus sign"
              aria-pressed={text.startsWith("-")}
              data-testid="button-toggle-sign"
            >
              +/-
            </button>
          )}
          <Input
            value={text}
            disabled={done}
            inputMode={block.kind === "numeric" ? "decimal" : "text"}
            placeholder={block.kind === "numeric" ? "Type a number" : "Type your answer"}
            onChange={e => { if (check.status === "wrong") check.retry(); setText(e.target.value); }}
            onKeyDown={e => { if (e.key === "Enter" && canSubmit && check.status === "idle") submit(); }}
            className={`h-14 flex-1 min-w-0 text-xl font-semibold rounded-2xl ${done ? (check.status === "correct" ? "border-emerald-500" : "border-sky-400") : ""}`}
            data-testid="input-lesson-answer"
          />
        </div>
      )}

      <CheckFooter
        check={check}
        canSubmit={canSubmit}
        onSubmit={submit}
        hint={block.hint}
        feedbackCorrect={block.feedbackCorrect}
        feedbackWrong={block.feedbackWrong}
        explanation={block.explanation}
        revealAnswer={
          block.kind === "mc" || block.kind === "multi"
            ? <p>Correct answer: <strong>{block.options?.filter(o => correctIds.has(o.id)).map(o => o.text).join(", ")}</strong></p>
            : <p>Correct answer: <strong>{Array.isArray(block.answer) ? block.answer[0] : String(block.answer)}</strong></p>
        }
      />
    </div>
  );
}
